import React, { useMemo } from 'react';
import { Button, DatePicker, InputNumber, Select, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { transportDatePickerProps } from './transportFormUtils';
import {
  equalSplit,
  roundSplitQuantity,
  SPLIT_QUANTITY_PRECISION,
  type TransportSplitProduct,
  type TransportSplitRow,
} from '../utils/transportSplit';
import TransportCapacityBadge from './TransportCapacityBadge';
import {
  calculateCargoItemsMetrics,
  deriveLdmCapacityIndicator,
  deriveWeightCapacityIndicator,
  type SharedTransportCargoItem,
} from './sharedTransportFormUtils';
import { formatUnit } from '../utils/units';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import { localeForLanguage } from '../utils/numberFormatting';

const { Text } = Typography;

interface TransportSplitTableProps {
  products: TransportSplitProduct[];
  value: TransportSplitRow[];
  canEditTransportCosts: boolean;
  currency?: string;
  onChange: (rows: TransportSplitRow[]) => void;
}

function toQuantity(value: unknown): number {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function formatQuantity(value: number, language?: string): string {
  return roundSplitQuantity(value).toLocaleString(localeForLanguage(language), {
    minimumFractionDigits: 0,
    maximumFractionDigits: SPLIT_QUANTITY_PRECISION,
  });
}

function buildEqualizedRows(
  products: TransportSplitProduct[],
  rows: TransportSplitRow[],
): TransportSplitRow[] {
  const sharesByProduct = new Map(
    products.map((product) => [product.key, equalSplit(product.quantity, rows.length)]),
  );

  return rows.map((row, index) => ({
    ...row,
    quantities: products.reduce<Record<string, number>>((quantities, product) => {
      quantities[product.key] = sharesByProduct.get(product.key)?.[index] || 0;
      return quantities;
    }, {}),
  }));
}

const TransportSplitTable: React.FC<TransportSplitTableProps> = ({
  products,
  value,
  canEditTransportCosts,
  currency,
  onChange,
}) => {
  const { t, i18n } = useTranslation(['transport']);

  // Reuse the shared cargo-metrics aggregator (single home for the piece →
  // m²/m³/LDM formula) by materializing each product at its per-truck quantity
  // into a cargo-item array. No formula lives here — display only.
  const formatMetricValue = (value: number, fractionDigits: number): string =>
    value.toLocaleString(localeForLanguage(i18n.language), {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  const buildTruckMetrics = (quantityByProductKey: Record<string, number>) => {
    const cargoItems: SharedTransportCargoItem[] = products.map((product) => ({
      orderId: product.orderId || '',
      orderProductId: product.orderProductId,
      productName: product.productName,
      quantity: toQuantity(quantityByProductKey[product.key]),
      unit: product.unit,
      grossWeight: product.grossWeight,
      width: product.width,
      height: product.height,
      length: product.length,
      thickness: product.thickness,
      // Package-based LDM needs the per-product packaging snapshot.
      piecesPerPackage: product.piecesPerPackage ?? null,
      packageLength: product.packageLength ?? null,
      packageWidth: product.packageWidth ?? null,
      packageStackLayers: product.packageStackLayers ?? null,
    }));
    return calculateCargoItemsMetrics(cargoItems);
  };

  const summarizeMetrics = (quantityByProductKey: Record<string, number>): string => {
    const metrics = buildTruckMetrics(quantityByProductKey);
    const packages = products.reduce(
      (sum, product) => sum + toQuantity(quantityByProductKey[product.key]),
      0,
    );

    return t('transport:split.metricsSummary', {
      packages: formatQuantity(packages, i18n.language),
      area: formatMetricValue(metrics.areaSqm, 2),
      volume: formatMetricValue(metrics.volume, 3),
      // Package-based LDM: "—" when incomplete or zero (no packaging data).
      ldm: metrics.ldmIncomplete || !metrics.rawLdm ? '—' : formatMetricValue(metrics.rawLdm, 2),
    });
  };

  const totalQuantityByProduct = useMemo(
    () => products.reduce<Record<string, number>>((totals, product) => {
      totals[product.key] = product.quantity;
      return totals;
    }, {}),
    [products],
  );

  const totalsByProduct = useMemo(() => (
    products.reduce<Record<string, number>>((totals, product) => {
      totals[product.key] = roundSplitQuantity(
        value.reduce((sum, row) => sum + toQuantity(row.quantities[product.key]), 0),
      );
      return totals;
    }, {})
  ), [products, value]);

  const updateRow = (rowKey: string, patch: Partial<TransportSplitRow>) => {
    onChange(value.map((row) => (row.key === rowKey ? { ...row, ...patch } : row)));
  };

  const updateQuantity = (row: TransportSplitRow, productKey: string, nextValue: number | null) => {
    updateRow(row.key, {
      quantities: {
        ...row.quantities,
        [productKey]: toQuantity(nextValue),
      },
    });
  };

  const columns: ColumnsType<TransportSplitRow> = [
    {
      title: t('transport:transport'),
      key: 'truck',
      width: 140,
      fixed: 'left',
      render: (_value, _record, index) => t('transport:split.truckLabel', { index: index + 1 }),
    },
    ...products.map((product) => ({
      title: product.productName,
      key: product.key,
      width: 180,
      render: (_value: unknown, record: TransportSplitRow) => (
        <InputNumber
          min={0}
          precision={SPLIT_QUANTITY_PRECISION}
          addonAfter={formatUnit(product.unit)}
          value={record.quantities[product.key] ?? 0}
          onChange={(nextValue) => updateQuantity(record, product.key, nextValue)}
          style={{ width: '100%' }}
          data-testid={`transport-split-truck-${record.key}-quantity-${product.key}`}
          {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
        />
      ),
    })),
    ...(canEditTransportCosts ? [{
      title: t('transport:split.rate'),
      key: 'transportRate',
      width: 170,
      render: (_value: unknown, record: TransportSplitRow) => (
        <Space.Compact style={{ width: '100%' }}>
          <InputNumber
            min={0}
            precision={2}
            value={record.transportRate ?? null}
            onChange={(nextValue) => updateRow(record.key, { transportRate: nextValue == null ? null : Number(nextValue) })}
            style={{ width: 'calc(100% - 76px)' }}
            data-testid={`transport-split-truck-${record.key}-rate`}
            {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
          />
          <Select
            value={record.currency || currency || 'PLN'}
            options={['PLN', 'EUR', 'USD'].map((value) => ({ value, label: value }))}
            onChange={(nextCurrency) => updateRow(record.key, { currency: nextCurrency })}
            style={{ width: 76 }}
            data-testid={`transport-split-truck-${record.key}-currency`}
          />
        </Space.Compact>
      ),
    }] : []),
    {
      title: t('transport:split.loadingDate'),
      key: 'loadingDate',
      width: 180,
      render: (_value, record) => (
        <DatePicker
          value={record.loadingDate ?? null}
          placeholder={t('transport:placeholders.selectDate')}
          onChange={(nextValue) => updateRow(record.key, { loadingDate: nextValue })}
          {...transportDatePickerProps}
        />
      ),
    },
    // Read-only per-truck cargo metrics, live-updating as quantities change.
    {
      title: t('transport:split.metricsColumn'),
      key: 'metrics',
      width: 220,
      render: (_value, record) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {summarizeMetrics(record.quantities)}
        </Text>
      ),
    },
    // F1 (trailer capacity indicator): per-truck utilization badge — each
    // split truck is its own trailer, so capacity is checked per row, not
    // against the whole split's combined total. Display-only, never blocks
    // save. Weight badge omitted when 0 (most lines have no grossWeight).
    {
      title: t('transport:capacity.column'),
      key: 'capacity',
      width: 160,
      render: (_value, record) => {
        const metrics = buildTruckMetrics(record.quantities);
        const ldmCapacity = deriveLdmCapacityIndicator(metrics.ldm, metrics.ldmIncomplete);
        const weightCapacity = deriveWeightCapacityIndicator(metrics.weight);
        if (!ldmCapacity && !weightCapacity) {
          return null;
        }
        return (
          <Space direction="vertical" size={2}>
            {ldmCapacity ? <TransportCapacityBadge indicator={ldmCapacity} unitLabel="LDM" precision={2} /> : null}
            {weightCapacity ? <TransportCapacityBadge indicator={weightCapacity} unitLabel="kg" precision={0} /> : null}
          </Space>
        );
      },
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Button onClick={() => onChange(buildEqualizedRows(products, value))}>
        {t('transport:split.equalize')}
      </Button>
      <Table<TransportSplitRow>
        rowKey="key"
        size="small"
        pagination={false}
        dataSource={value}
        columns={columns}
        scroll={{ x: Math.max(940, 540 + products.length * 180) }}
        footer={() => (
          <Space direction="vertical" size={4}>
            <Text strong style={{ fontSize: 12 }}>
              {`${t('transport:split.metricsTotalLabel')}: ${summarizeMetrics(totalQuantityByProduct)}`}
            </Text>
            {products.map((product) => {
              const done = totalsByProduct[product.key] || 0;
              const total = roundSplitQuantity(product.quantity);
              const remaining = roundSplitQuantity(total - done);
              const unit = formatUnit(product.unit);

              return (
                <Space key={product.key} wrap>
                  <Text>
                    {t('transport:split.distributed', {
                      done: formatQuantity(done, i18n.language),
                      total: formatQuantity(total, i18n.language),
                      unit,
                    })}
                  </Text>
                  {remaining !== 0 ? (
                    <Text type="danger">
                      {remaining > 0
                        ? t('transport:split.remaining', {
                          value: formatQuantity(remaining, i18n.language),
                          unit,
                        })
                        : t('transport:split.exceeded', {
                          value: formatQuantity(Math.abs(remaining), i18n.language),
                          unit,
                        })}
                    </Text>
                  ) : null}
                </Space>
              );
            })}
          </Space>
        )}
      />
    </Space>
  );
};

export default TransportSplitTable;
